import React, { useMemo, useState } from 'react';
import { LICENSE_PERMIT_TYPES } from './config/trackers';

/**
 * Licenses & Permits — timeline view (spec § 16).
 *
 * Renders inside the Renewals tab when tracker.customView === 'licenses'.
 * Group permits by location (account.label); each location is a collapsible
 * accordion. Permit rows are sorted by days-until-expiration ascending. A
 * detail popup lets the user mark a permit renewed, which bumps issued/expires
 * by the configured frequency and saves through api.saveAccount.
 */

const PERMIT_TYPE_BY_ID = LICENSE_PERMIT_TYPES.reduce((acc, p) => {
    acc[p.id] = p;
    return acc;
}, {});

function todayIso() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseIso(iso) {
    if (!iso) return null;
    try {
        const d = new Date(`${iso}T00:00`);
        if (Number.isNaN(d.getTime())) return null;
        return d;
    } catch (e) {
        return null;
    }
}

function daysUntil(iso) {
    const d = parseIso(iso);
    if (!d) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function formatPretty(iso) {
    const d = parseIso(iso);
    if (!d) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Bucket a permit by days-until-expiration. */
function permitState(permit) {
    const f = permit.fields || {};
    if ((f.frequency || '').toLowerCase().includes('lifetime')
        || !f.expires) {
        return 'lifetime';
    }
    const dl = daysUntil(f.expires);
    if (dl === null) return 'lifetime';
    if (dl < 0) return 'expired';
    if (dl <= 30) return 'urgent';
    if (dl <= 90) return 'soon';
    return 'ok';
}

function moneyToNumber(s) {
    const n = Number(String(s || '').replace(/[^\d.]/g, ''));
    return Number.isFinite(n) ? n : 0;
}

function bumpDate(iso, frequency) {
    const d = parseIso(iso);
    if (!d) return iso;
    const f = (frequency || 'Annual').toLowerCase();
    let years = 1;
    if (f.includes('biennial')) years = 2;
    else if (f.includes('triennial')) years = 3;
    else if (f.includes('5')) years = 5;
    d.setFullYear(d.getFullYear() + years);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function LicensesView({ accounts, api, showToast }) {
    const [openLoc, setOpenLoc] = useState(null);
    const [detailTarget, setDetailTarget] = useState(null);

    // Group accounts by location label.
    const groups = useMemo(() => {
        const map = new Map();
        accounts.forEach((a) => {
            if (a.status !== 'active') return;
            const key = a.label || '(unnamed)';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(a);
        });
        // Sort permits within each group by days-until-expiration ascending.
        const out = [];
        Array.from(map.entries()).forEach(([loc, list]) => {
            const sorted = [...list].sort((a, b) => {
                const da = daysUntil(a.fields?.expires);
                const db = daysUntil(b.fields?.expires);
                if (da === null && db === null) return 0;
                if (da === null) return 1;
                if (db === null) return -1;
                return da - db;
            });
            out.push({ loc, permits: sorted });
        });
        out.sort((a, b) => a.loc.localeCompare(b.loc));
        return out;
    }, [accounts]);

    const stats = useMemo(() => {
        let overdue = 0;
        let dueSoon = 0;
        let active = 0;
        let annualCost = 0;
        accounts.forEach((a) => {
            if (a.status !== 'active') return;
            active += 1;
            annualCost += moneyToNumber(a.fields?.renewalCost);
            const s = permitState(a);
            if (s === 'expired') overdue += 1;
            else if (s === 'urgent' || s === 'soon') dueSoon += 1;
        });
        return {
            overdue,
            dueSoon,
            active,
            total: accounts.filter((a) => a.status === 'active').length,
            annualCost,
        };
    }, [accounts]);

    if (accounts.length === 0) {
        return (
            <div className="bt-empty">
                No licenses yet. Click <strong>+ Add Renewal</strong> to add one.
            </div>
        );
    }

    return (
        <>
            {/* Stats strip */}
            <div className="bt-lic-stats">
                <div className="bt-lic-stat overdue">
                    <div className="bt-lic-stat-label">
                        <span role="img" aria-label="Overdue">⚠</span> Overdue
                    </div>
                    <div className="bt-lic-stat-value">{stats.overdue}</div>
                </div>
                <div className="bt-lic-stat soon">
                    <div className="bt-lic-stat-label">
                        <span role="img" aria-label="Due Soon">🔔</span> Due Soon
                    </div>
                    <div className="bt-lic-stat-value">{stats.dueSoon}</div>
                </div>
                <div className="bt-lic-stat active">
                    <div className="bt-lic-stat-label">
                        <span role="img" aria-label="Active">✓</span> Active
                    </div>
                    <div className="bt-lic-stat-value">{stats.active}</div>
                </div>
                <div className="bt-lic-stat total">
                    <div className="bt-lic-stat-label">Total</div>
                    <div className="bt-lic-stat-value">{stats.total}</div>
                </div>
                <div className="bt-lic-stat cost">
                    <div className="bt-lic-stat-label">Annual Cost</div>
                    <div className="bt-lic-stat-value">${stats.annualCost.toLocaleString()}</div>
                </div>
            </div>

            <div className="bt-loc-list" style={{ marginTop: 12 }}>
                {groups.map((g) => (
                    <LocationCard
                        key={g.loc}
                        group={g}
                        open={openLoc === g.loc}
                        onToggle={() => setOpenLoc(openLoc === g.loc ? null : g.loc)}
                        onPermitClick={(permit) => setDetailTarget(permit)}
                    />
                ))}
            </div>

            {detailTarget && (
                <LicenseDetailPopup
                    permit={detailTarget}
                    onClose={() => setDetailTarget(null)}
                    onMarkRenewed={() => {
                        const f = detailTarget.fields || {};
                        const newIssued = todayIso();
                        const newExpires = bumpDate(
                            f.expires || todayIso(),
                            f.frequency || 'Annual',
                        );
                        const payload = {
                            accountId: detailTarget.accountId,
                            trackerKey: 'licenses',
                            label: detailTarget.label,
                            belongs: detailTarget.belongs,
                            frequency: 'asNeeded',
                            fields: {
                                ...f,
                                issued: newIssued,
                                expires: newExpires,
                            },
                            paymentMethod: detailTarget.paymentMethod,
                            months: detailTarget.months,
                            subTaxes: detailTarget.subTaxes,
                        };
                        api.saveAccount(payload, (err) => {
                            if (!err) {
                                showToast('Permit renewed', 'success');
                                setDetailTarget(null);
                            }
                        });
                    }}
                />
            )}
        </>
    );
}

function LocationCard({ group, open, onToggle, onPermitClick }) {
    const overdue = group.permits.filter((p) => permitState(p) === 'expired').length;
    const dueSoon = group.permits.filter(
        (p) => permitState(p) === 'urgent' || permitState(p) === 'soon',
    ).length;

    return (
        <div className={`bt-loc-card${open ? ' expanded' : ''}`}>
            <div
                className="bt-loc-head"
                onClick={onToggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onToggle()}
            >
                <div className="bt-loc-info">
                    <div className="bt-loc-name">{group.loc}</div>
                    <div className="bt-loc-meta">
                        <span className="bt-tag">
                            {group.permits.length} renewal{group.permits.length === 1 ? '' : 's'}
                        </span>
                        {overdue > 0 && (
                            <span className="bt-tag alert">
                                <span role="img" aria-label="Overdue">⚠</span> {overdue} overdue
                            </span>
                        )}
                        {dueSoon > 0 && (
                            <span className="bt-tag warn">
                                <span role="img" aria-label="Due Soon">🔔</span> {dueSoon} due soon
                            </span>
                        )}
                    </div>
                </div>
                <div className="bt-loc-chevron" aria-hidden="true">▼</div>
            </div>

            {open && (
                <div className="bt-loc-body">
                    <div className="bt-permit-list">
                        {group.permits.map((p) => (
                            <PermitRow
                                key={p.accountId}
                                permit={p}
                                onClick={() => onPermitClick(p)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function PermitRow({ permit, onClick }) {
    const f = permit.fields || {};
    const state = permitState(permit);
    const dl = daysUntil(f.expires);
    const meta = PERMIT_TYPE_BY_ID[f.permitTypeId];
    const icon = meta?.icon || '📜';
    const dateState = state;

    let daysLabel = '';
    if (state === 'lifetime') daysLabel = 'Lifetime';
    else if (state === 'expired') daysLabel = `${Math.abs(dl)}d expired`;
    else if (dl === 0) daysLabel = 'Today';
    else daysLabel = `${dl}d left`;

    return (
        <div
            className={`bt-permit-row ${state}`}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
        >
            <div className="bt-permit-icon" role="img" aria-label="Permit type">{icon}</div>
            <div className="bt-permit-info">
                <div className="bt-permit-title">
                    {f.permitType || meta?.label || 'Permit'}
                </div>
                <div className="bt-permit-sub">
                    {f.authority || meta?.authority || '—'}
                    {f.permitNumber ? ` · ${f.permitNumber}` : ''}
                </div>
                <div className="bt-permit-dates">
                    <span className={`bt-permit-pill ${dateState}`}>
                        Issued {formatPretty(f.issued)}
                    </span>
                    <span className={`bt-permit-pill ${dateState}`}>
                        Expires {formatPretty(f.expires)}
                    </span>
                </div>
            </div>
            <div className="bt-permit-right">
                <div className={`bt-permit-days ${state}`}>{daysLabel}</div>
                <div className="bt-permit-cost">
                    {f.renewalCost || '—'} · {f.frequency || 'Annual'}
                </div>
            </div>
        </div>
    );
}

function LicenseDetailPopup({ permit, onClose, onMarkRenewed }) {
    const f = permit.fields || {};
    const state = permitState(permit);
    const meta = PERMIT_TYPE_BY_ID[f.permitTypeId];
    const dl = daysUntil(f.expires);

    let badgeText = 'Active';
    if (state === 'expired') badgeText = `Expired ${Math.abs(dl)}d ago`;
    else if (state === 'urgent') badgeText = `${dl}d left`;
    else if (state === 'soon') badgeText = `${dl}d left`;
    else if (state === 'lifetime') badgeText = 'Lifetime';
    else if (dl !== null) badgeText = `${dl}d left`;

    return (
        <div className="bt-overlay" onClick={onClose}>
            <div
                className="bt-modal"
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bt-modal-head">
                    <h2>
                        <span role="img" aria-label="Permit">{meta?.icon || '📜'}</span>{' '}
                        {f.permitType || meta?.label || 'Permit'}
                    </h2>
                    <button
                        className="bt-modal-close"
                        onClick={onClose}
                        type="button"
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>
                <div className="bt-lic-detail-sub">
                    {permit.label}
                    {f.authority ? ` — ${f.authority}` : ''}
                </div>
                <div className={`bt-lic-detail-badge ${state}`}>{badgeText}</div>

                <div className="bt-detail-grid">
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Permit #</div>
                        <div className="bt-detail-value">{f.permitNumber || '—'}</div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Issued</div>
                        <div className="bt-detail-value">{formatPretty(f.issued)}</div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Expires</div>
                        <div className="bt-detail-value">{formatPretty(f.expires)}</div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Renewal Cost</div>
                        <div className="bt-detail-value">{f.renewalCost || '—'}</div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Frequency</div>
                        <div className="bt-detail-value">{f.frequency || 'Annual'}</div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Status</div>
                        <div className="bt-detail-value">{badgeText}</div>
                    </div>
                </div>

                <div className="bt-modal-actions">
                    <button
                        className="bt-btn bt-btn-ghost"
                        onClick={onClose}
                        type="button"
                    >
                        Close
                    </button>
                    <button
                        className="bt-btn bt-btn-save"
                        onClick={onMarkRenewed}
                        type="button"
                    >
                        <span role="img" aria-label="Renewed">✓</span> Mark Renewed
                    </button>
                </div>
            </div>
        </div>
    );
}

export default LicensesView;
